import React, { useCallback, useEffect, useState } from "react";
import { Button, Surface } from "@heroui/react";
import { LoadingState } from "../components/LoadingState";
import { StandaloneCatalogImport } from "../components/StandaloneCatalogImport";
import { PortalCheckbox } from "../components/form/PortalCheckbox";
import { PortalTextField } from "../components/form/PortalTextField";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { navigatePortal } from "../lib/navigation";
import { showErrorToast, showSuccessToast } from "../lib/toast";

function replaceEmbedToken(value, token) {
  if (!value) return "";
  return value.replace(/YOUR_EMBED_TOKEN/g, token || "YOUR_EMBED_TOKEN");
}

export function GuaranteesPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [embedSnippet, setEmbedSnippet] = useState("");
  const [embedToken, setEmbedToken] = useState("");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    external_key: "",
    title: "",
    garan_years: "5",
    garan_brand: "",
    garan_model: "",
    garan_enabled: true
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [s, email, setup, embed, productPayload] = await Promise.all([
      apiFetch("/api/guarantees/status").catch(() => null),
      apiFetch("/api/guarantees/email_snippet").catch(() => ({ snippet: "" })),
      apiFetch("/api/portal/setup_status").catch(() => null),
      apiFetch("/api/portal/embed_snippet").catch(() => null),
      apiFetch("/api/guarantees/products?limit=50").catch(() =>
        apiFetch("/api/portal/standalone_products?limit=50").catch(() => ({ products: [] }))
      )
    ]);
    setStatus(s);
    setSnippet(email?.snippet || "");
    setEmbedToken(setup?.embed_token || "");
    setEmbedSnippet(replaceEmbedToken(embed?.guarantees_script_html, setup?.embed_token));
    setProducts(productPayload?.products || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch(() => {
        if (!cancelled) showErrorToast(t("guarantees.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, t]);

  const enableModule = async () => {
    try {
      await apiFetch("/api/portal/modules", {
        method: "PATCH",
        body: JSON.stringify({ module_key: "guarantees", enabled: true })
      });
      showSuccessToast(t("guarantees.saved"));
      await load();
    } catch {
      showErrorToast(t("guarantees.saveError"));
    }
  };

  const createProduct = async () => {
    if (!form.external_key.trim()) {
      showErrorToast(t("guarantees.productKeyRequired"));
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/api/portal/standalone_products", {
        method: "POST",
        body: JSON.stringify({
          standalone_product: {
            external_key: form.external_key.trim(),
            title: form.title.trim() || form.external_key.trim(),
            garan_years: form.garan_years,
            garan_brand: form.garan_brand,
            garan_model: form.garan_model,
            garan_enabled: form.garan_enabled
          }
        })
      });
      setForm({
        external_key: "",
        title: "",
        garan_years: "5",
        garan_brand: "",
        garan_model: "",
        garan_enabled: true
      });
      showSuccessToast(t("guarantees.saved"));
      await load();
    } catch (e) {
      showErrorToast(e?.message || t("guarantees.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const updateProduct = async (product, patch) => {
    try {
      await apiFetch("/api/guarantees/products", {
        method: "PATCH",
        body: JSON.stringify({
          product_gid: product.gid || `standalone://${product.external_key}`,
          ...patch
        })
      });
      await load();
    } catch {
      showErrorToast(t("guarantees.saveError"));
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      showSuccessToast(t("onboarding.snippetCopied"));
    } catch {
      showErrorToast(t("onboarding.copySnippet"));
    }
  };

  if (loading) return <LoadingState label={t("common.loading")} />;

  return (
    <div className="portal-page-stack">
      <Surface className="portal-page-surface">
        <h2 className="portal-panel__title">{t("guarantees.pageTitle")}</h2>

        {!status?.subscribed ? (
          <div className="portal-banner portal-banner--info">
            <p className="font-medium">{t("guarantees.billingRequiredHeading")}</p>
            <p className="mt-1">{t("guarantees.billingRequiredBody")}</p>
            <div className="portal-panel__actions mt-3">
              <Button className="portal-accent-button" onPress={enableModule}>
                {t("guarantees.enableStandalone")}
              </Button>
              <Button variant="secondary" onPress={() => navigatePortal("/portal/billing")}>
                {t("guarantees.goToBilling")}
              </Button>
            </div>
          </div>
        ) : null}

        <h3 className="portal-panel__section-title">{t("guarantees.noticeSection")}</h3>
        <p className="portal-panel__description">
          {status?.block_present ? t("guarantees.standaloneReady") : t("guarantees.blockMissing")}
        </p>
        {status?.theme_editor_url ? (
          <div className="portal-panel__actions">
            <Button as="a" href={status.theme_editor_url} target="_blank" rel="noreferrer" variant="secondary">
              {t("guarantees.openThemeEditor")}
            </Button>
          </div>
        ) : null}
      </Surface>

      {embedSnippet ? (
        <Surface className="portal-page-surface">
          <h3 className="portal-panel__section-title">{t("guarantees.embedSection")}</h3>
          <p className="portal-panel__description">{t("guarantees.embedHelp")}</p>
          <div className="portal-banner portal-banner--info">
            <p className="font-medium m-0">{t("guarantees.embedMatchHeading")}</p>
            <p className="mt-1 mb-0">{t("guarantees.embedMatchBody")}</p>
          </div>
          {embedToken ? (
            <div className="portal-panel__code-wrap">
              <h3 className="portal-panel__section-title">{t("settings.embedTokenTitle")}</h3>
              <pre className="portal-panel__code">{embedToken}</pre>
            </div>
          ) : null}
          <div className="portal-panel__code-wrap">
            <pre className="portal-panel__code">{embedSnippet}</pre>
            <Button variant="secondary" onPress={copyEmbed}>
              {t("onboarding.copySnippet")}
            </Button>
          </div>
        </Surface>
      ) : null}

      <StandaloneCatalogImport focus="garan" onImported={() => load()} />

      <Surface className="portal-page-surface">
        <h3 className="portal-panel__section-title">{t("guarantees.garanSection")}</h3>
        <p className="portal-panel__description">{t("guarantees.standaloneGaranHelp")}</p>

        <div className="grid gap-4 md:grid-cols-2">
          <PortalTextField
            label={t("guarantees.colKey")}
            value={form.external_key}
            onChange={(v) => setForm((f) => ({ ...f, external_key: v }))}
          />
          <PortalTextField
            label={t("guarantees.colTitle")}
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
          />
          <PortalTextField
            label={t("guarantees.colYears")}
            value={form.garan_years}
            onChange={(v) => setForm((f) => ({ ...f, garan_years: v }))}
          />
          <PortalTextField
            label={t("guarantees.colBrand")}
            value={form.garan_brand}
            onChange={(v) => setForm((f) => ({ ...f, garan_brand: v }))}
          />
          <PortalTextField
            label={t("guarantees.colModel")}
            value={form.garan_model}
            onChange={(v) => setForm((f) => ({ ...f, garan_model: v }))}
          />
          <div className="portal-checkbox-row md:self-end">
            <PortalCheckbox
              isSelected={form.garan_enabled}
              onChange={(checked) => setForm((f) => ({ ...f, garan_enabled: checked }))}
            >
              {t("guarantees.garanEnabled")}
            </PortalCheckbox>
          </div>
        </div>

        <div className="portal-panel__actions">
          <Button
            variant="primary"
            className="portal-accent-button"
            isDisabled={saving}
            onPress={createProduct}
          >
            {t("guarantees.addProduct")}
          </Button>
        </div>

        {products.length === 0 ? (
          <p className="portal-panel__description">{t("guarantees.noProducts")}</p>
        ) : (
          <div className="portal-data-grid__table-wrap overflow-auto">
            <table className="portal-data-grid__table w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr>
                  <th className="py-2 pr-3">{t("guarantees.colTitle")}</th>
                  <th className="py-2 pr-3">{t("guarantees.colYears")}</th>
                  <th className="py-2 pr-3">{t("guarantees.colBrand")}</th>
                  <th className="py-2 pr-3">{t("guarantees.colModel")}</th>
                  <th className="py-2">{t("guarantees.colEnabled")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.gid || product.id}>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{product.title || product.external_key}</div>
                      <div className="text-xs opacity-70">{product.external_key}</div>
                    </td>
                    <td className="py-2 pr-3">{product.garan_years || "—"}</td>
                    <td className="py-2 pr-3">{product.garan_brand || "—"}</td>
                    <td className="py-2 pr-3">{product.garan_model || "—"}</td>
                    <td className="py-2">
                      <PortalCheckbox
                        aria-label={t("guarantees.garanEnabled")}
                        isSelected={Boolean(product.garan_enabled)}
                        onChange={(checked) => updateProduct(product, { enabled: checked })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Surface>

      <Surface className="portal-page-surface">
        <h3 className="portal-panel__section-title">{t("guarantees.proofSection")}</h3>
        <p className="portal-panel__description">{t("guarantees.emailSnippetHelp")}</p>
        <pre className="portal-panel__code">{snippet || t("guarantees.emailSnippetEmpty")}</pre>
      </Surface>
    </div>
  );
}
